import React, { useEffect, useRef } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { basicSetup } from 'codemirror';
import { python } from '@codemirror/lang-python';
import { keymap } from '@codemirror/view';
import { indentWithTab } from '@codemirror/commands';
import { lintGutter } from '@codemirror/lint';
import { indentUnit } from '@codemirror/language';

interface CodeEditorProps {
  code: string;
  setCode: (code: string) => void;
}

const CodeEditor: React.FC<CodeEditorProps> = ({ code, setCode }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const editorViewRef = useRef<EditorView | null>(null);

  useEffect(() => {
    if (editorRef.current) {
      const initialCode = ensureMinimumLines(code, 25);

      const state = EditorState.create({
        doc: initialCode,
        extensions: [
          basicSetup,
          python(),
          indentUnit.of('    '),  // Use 4 spaces for indentation
          keymap.of([indentWithTab]),  // Handle Tab key
          lintGutter(),
          EditorView.lineWrapping,
          EditorView.theme({
            "&": { height: "400px" },
            ".cm-scroller": { overflow: "auto" },
          }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              setCode(update.state.doc.toString());
            }
          }),
        ],
      });

      const view = new EditorView({
        state,
        parent: editorRef.current,
      });

      editorViewRef.current = view;

      return () => {
        view.destroy();
      };
    }
  }, []);

  useEffect(() => {
    if (editorViewRef.current && code !== editorViewRef.current.state.doc.toString()) {
      editorViewRef.current.dispatch({
        changes: { from: 0, to: editorViewRef.current.state.doc.length, insert: code }
      });
    }
  }, [code]);

  return <div ref={editorRef} className="code-editor" />;
};

function ensureMinimumLines(code: string, minLines: number): string {
  const lines = code.split('\n');
  const currentLines = lines.length;
  if (currentLines < minLines) {
    const placeholderLines = Array(minLines - currentLines).fill('');
    return [...lines, ...placeholderLines].join('\n');
  }
  return code;
}

export default CodeEditor;